import { Command } from 'commander';
import chalk from 'chalk';
import inquirer from 'inquirer';
import { logger } from '../lib/logger.js';
import {
  saveLicenseKey,
  loadLicenseKey,
  validateLicenseKey,
  validateCurrentLicense,
  activateLicense,
  deactivateLicense,
  formatLicenseInfo,
  detectJwtFormat,
} from '../lib/license.js';
import { loadCache } from '../lib/license-cache.js';
import { getMachineIdentity } from '../lib/device-id.js';
import {
  LicenseClient,
  getLicenseServerUrl,
  saveApiKey,
  removeApiKey,
  getApiKey,
} from '../lib/license-client.js';

export function createLicenseCommand(): Command {
  const license = new Command('license')
    .description('Manage SmartStack CLI license');

  // ── activate <key> ──────────────────────────────────────────────────────
  license
    .command('activate <key>')
    .description('Activate a license key on this machine')
    .action(async (key: string) => {
      logger.info('Activating license...\n');

      const result = await activateLicense(key);

      if (!result.valid) {
        logger.error(`Activation Failed: ${result.error || 'Invalid license'}`);
        process.exit(1);
      }

      logger.success('License activated successfully!');
      console.log(formatLicenseInfo(result.license!, result.source));

      // Show machine info
      const identity = await getMachineIdentity();
      console.log(chalk.gray(`  Machine:     ${identity.machineName}`));
      console.log(chalk.gray(`  Fingerprint: ${identity.fingerprint.substring(0, 12)}...`));
      console.log(chalk.gray(`  Instance:    ${identity.instanceId}`));

      // Show warnings
      for (const warning of result.warnings) {
        logger.warning(warning);
      }
    });

  // ── status ──────────────────────────────────────────────────────────────
  license
    .command('status')
    .description('Show current license status')
    .action(async () => {
      const result = await validateCurrentLicense();

      if (!result.valid) {
        logger.error(`No Valid License: ${result.error || 'License not found'}`);
        logger.info('\nTo activate a license:');
        logger.info('  smartstack license activate <your-license-key>');
        logger.info('\nTo request a trial:');
        logger.info('  smartstack license trial');
        logger.info('\nTo purchase a license:');
        logger.info('  https://atlashub.io/products/smartstack-cli');
        process.exit(1);
      }

      console.log(formatLicenseInfo(result.license!, result.source));

      // Show extended info
      const licenseKey = await loadLicenseKey();
      const format = licenseKey ? detectJwtFormat(licenseKey) : 'unknown';
      const cache = await loadCache();

      console.log(chalk.cyan('  Extended Info:'));
      console.log(chalk.gray(`  Format:       ${format === 'legacy' ? 'Legacy (RS256)' : 'New (RS512)'}`));
      console.log(chalk.gray(`  Environment:  ${result.license!.environment || 'N/A'}`));
      console.log(chalk.gray(`  Source:       ${result.source}`));

      if (cache.activation) {
        console.log(chalk.gray(`  Activated:    ${cache.activation.activatedAt?.split('T')[0] || 'N/A'}`));
        console.log(chalk.gray(`  Heartbeat:    every ${cache.activation.heartbeatIntervalHours}h`));
        console.log(chalk.gray(`  Grace Period: ${cache.activation.gracePeriodDays} days`));
      }

      if (cache.lastHeartbeat) {
        console.log(chalk.gray(`  Last Beat:    ${cache.lastHeartbeat}`));
      }

      console.log();

      // Warnings
      for (const warning of result.warnings) {
        logger.warning(warning);
      }

      if (result.license!.daysRemaining <= 30 && !result.license!.isPerpetual) {
        logger.warning(`License expires in ${result.license!.daysRemaining} days`);
        logger.info('Renew at: https://atlashub.io/products/smartstack-cli');
      }
    });

  // ── deactivate ──────────────────────────────────────────────────────────
  license
    .command('deactivate')
    .description('Remove the current license from this machine')
    .action(async () => {
      const key = await loadLicenseKey();

      if (!key) {
        logger.warning('No license is currently active');
        return;
      }

      const { confirm } = await inquirer.prompt<{ confirm: boolean }>([
        {
          type: 'confirm',
          name: 'confirm',
          message: 'Are you sure you want to deactivate the license on this machine?',
          default: false,
        },
      ]);

      if (!confirm) {
        logger.info('Deactivation cancelled');
        return;
      }

      await deactivateLicense();
      logger.success('License deactivated successfully');
    });

  // ── verify <key> ────────────────────────────────────────────────────────
  license
    .command('verify <key>')
    .description('Verify a license key without activating it')
    .action(async (key: string) => {
      logger.info('Verifying license key...\n');

      const format = detectJwtFormat(key);
      const result = validateLicenseKey(key);

      if (!result.valid) {
        logger.error(`License Invalid: ${result.error || 'Unknown error'}`);
        process.exit(1);
      }

      logger.success(`License is valid! (format: ${format === 'legacy' ? 'Legacy RS256' : 'New RS512'})`);
      console.log(formatLicenseInfo(result.license!, result.source));

      for (const warning of result.warnings) {
        logger.warning(warning);
      }
    });

  // ── trial ───────────────────────────────────────────────────────────────
  license
    .command('trial')
    .description('Request a free trial license')
    .action(async () => {
      logger.info('SmartStack CLI Trial Registration\n');

      const answers = await inquirer.prompt<{
        fullName: string;
        email: string;
        company: string;
        teamSize: string;
      }>([
        {
          type: 'input',
          name: 'fullName',
          message: 'Your full name:',
          validate: (v: string) => v.trim().length > 0 || 'Name is required',
        },
        {
          type: 'input',
          name: 'email',
          message: 'Your email address:',
          validate: (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Valid email required',
        },
        {
          type: 'input',
          name: 'company',
          message: 'Company name:',
          validate: (v: string) => v.trim().length > 0 || 'Company is required',
        },
        {
          type: 'list',
          name: 'teamSize',
          message: 'Team size:',
          choices: ['1-5', '6-20', '21-50', '51-200', '200+'],
        },
      ]);

      const spinner = logger.spinner('Registering trial...');

      try {
        const baseUrl = await getLicenseServerUrl();
        const client = new LicenseClient(baseUrl);
        const response = await client.registerTrial(answers);

        spinner.succeed('Trial registered!');

        // Find the CLI license token
        const knownProducts = ['smartstack-cli', 'smartstackcli', 'standard', 'plus', 'premium'];
        const cliLicense = response.licenses.find(l =>
          knownProducts.includes(l.product.toLowerCase()),
        ) || (response.licenses.length === 1 ? response.licenses[0] : undefined);
        if (!cliLicense) {
          const returned = response.licenses.map(l => l.product).join(', ');
          logger.error(`No CLI license found in trial response. Server returned: [${returned}]`);
          process.exit(1);
        }

        // Auto-activate
        logger.info('\nActivating trial license...');
        const activationResult = await activateLicense(cliLicense.token);

        if (activationResult.valid) {
          logger.success('Trial license activated!');
          console.log(formatLicenseInfo(activationResult.license!, activationResult.source));
          console.log(chalk.gray(`  Trial expires: ${response.expiresAt.split('T')[0]}`));
        } else {
          // Fallback: save the key even if activation fails
          await saveLicenseKey(cliLicense.token);
          logger.warning(`Trial created but activation failed: ${activationResult.error}`);
          logger.info('The license key has been saved. Try running any command to trigger online validation.');
        }
      } catch (err: unknown) {
        spinner.fail('Trial registration failed');
        const apiErr = err as { status?: number; message?: string };
        if (apiErr?.status === 429) {
          logger.error('Rate limited. A trial has already been created for this email or IP.');
        } else {
          logger.error(apiErr?.message || 'Unknown error');
        }
        process.exit(1);
      }
    });

  // ── api-key ─────────────────────────────────────────────────────────────
  const apiKeyCmd = license
    .command('api-key')
    .description('Manage API key for CI/CD authentication');

  apiKeyCmd
    .command('set <key>')
    .description('Configure an API key for machine-to-machine authentication')
    .action(async (key: string) => {
      if (!key.startsWith('sk_')) {
        logger.error('Invalid API key format. Keys should start with "sk_"');
        process.exit(1);
      }

      await saveApiKey(key);
      logger.success('API key configured successfully');
      console.log(chalk.gray(`  Key prefix: ${key.substring(0, 10)}...`));
    });

  apiKeyCmd
    .command('remove')
    .description('Remove the stored API key')
    .action(async () => {
      const existing = await getApiKey();
      if (!existing) {
        logger.warning('No API key is currently configured');
        return;
      }

      await removeApiKey();
      logger.success('API key removed');
    });

  apiKeyCmd
    .command('status')
    .description('Check if an API key is configured')
    .action(async () => {
      const existing = await getApiKey();
      if (existing) {
        logger.success(`API key configured: ${existing.substring(0, 10)}...`);
      } else {
        logger.info('No API key configured');
        logger.info('  Set one with: smartstack license api-key set <key>');
      }
    });

  return license;
}
